// OPTIMIZATION: Use centralized Parse initialization
import Parse from './initializeParse'
import { parseCloudRun } from './parseCloud'
import { Company, CompanyUser, CompanyUserRole } from '../core/models/Company'
import { getServerBaseUrl } from '@/utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'

// ---------------------------------------------------------------------------
// In-memory + localStorage caches for hot-path queries.
// These values change very rarely during a session, so a short TTL
// eliminates redundant network round-trips on every component mount.
// ---------------------------------------------------------------------------
// This browser's own actions clear this cache (updates, sign-out, access revoked, reactivation,
// self-serve creation). Changes made by other users are not cleared: they show up when the TTL
// expires, which is safe because the cloud re-authorises every action itself.
const CACHE_TTL_MS = 5 * 60_000 // 5 minutes

/**
 * `lookupFailed` marks a read that could not be answered (no session, network, server or session
 * error), as opposed to the cloud answering `success: false` (no company, company disabled). The
 * second is a normal state for a standalone user; callers that must tell them apart read the flag
 * rather than match the error text.
 */
type CurrentUserCompanyResult = {
  success: boolean
  company?: Company
  user_role?: string
  /** The role's current name, when cloud sends it; an old cloud does not (SC-439). */
  role_name?: string | null
  /** The fallback role's key and whether the user's role is that fallback; absent on an old cloud. */
  effective_role_key?: string | null
  role_is_default?: boolean
  permissions?: string[]
  error?: string
  lookupFailed?: boolean
  /**
   * The cloud answered that the user belongs to no company (`no_company: true` on its reply). It
   * is a normal, stable answer for a standalone user, so it is the one `success: false` reply that
   * is cached. An older cloud sends no flag and its reply is not cached.
   */
  noCompany?: boolean
}

interface CacheEntry<T> { value: T; ts: number; uid?: string | null }

let _companyCache: CacheEntry<CurrentUserCompanyResult> | null = null
let _companyInflight: Promise<CurrentUserCompanyResult> | null = null

let _adminCache: CacheEntry<boolean> | null = null
let _adminInflight: Promise<boolean> | null = null

function currentUserId(): string | null {
  try { return Parse.User.current()?.id ?? null } catch { return null }
}

/**
 * These caches live under fixed keys (`wiggin_company_data`, `wiggin_is_admin`) shared by every
 * user of the browser, and `clearCache()` only actually runs on logout. So an identity switch
 * that does NOT log out — redeeming an invitation while an admin is signed in, which calls
 * `Parse.User.become()` on the invitee's token — would otherwise serve the *admin's* company to
 * the invitee for the rest of the TTL, surviving the `window.location.reload()` because
 * localStorage does. Stamping the owner makes an entry self-invalidating instead of relying on
 * every future caller to remember `clearCache()`.
 *
 * Only a definite mismatch rejects. Parse can restore its current user asynchronously, so when
 * either side's identity is unknown the entry is still served — exactly as before — rather than
 * turning a transient null into a spurious "Not authenticated".
 */
function ownedByCurrentUser(entryUid: string | null | undefined): boolean {
  const uid = currentUserId()
  if (!uid || !entryUid) return true
  return uid === entryUid
}

function makeEntry<T>(value: T): CacheEntry<T> {
  return makeEntryFor(value, currentUserId())
}

/**
 * Stamps an entry with a *given* owner rather than whoever is current right now.
 *
 * `makeEntry` reads the identity at the moment it is called, which for a network result is
 * the moment the request *finishes*. That is the wrong moment. Redeeming an invitation while
 * an admin is signed in calls `Parse.User.become()` on the invitee's token, so an admin
 * `getCurrentUserCompany` still in flight resolves *after* the switch and stamps the admin's
 * company with the invitee's uid — making `ownedByCurrentUser` approve it and recreating,
 * through a race, exactly the cross-user leak the uid stamp was added to prevent.
 * `clearCache()` cannot help: it nulls the promise variable but the promise keeps running.
 *
 * So callers capture the requester before starting and cache only if that identity still
 * holds. [SC-313]
 */
function makeEntryFor<T>(value: T, uid: string | null): CacheEntry<T> {
  return { value, ts: Date.now(), uid }
}

/**
 * Whether a result fetched on behalf of `requesterUid` may still be cached.
 *
 * Only a *definite* change rejects, matching `ownedByCurrentUser`: Parse restores its current
 * user asynchronously, so a transient null on either side must not throw away a good result.
 */
function stillSameRequester(requesterUid: string | null): boolean {
  const uid = currentUserId()
  if (!uid || !requesterUid) return true
  return uid === requesterUid
}

function isFresh<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
  return (
    entry !== null &&
    Date.now() - entry.ts < CACHE_TTL_MS &&
    ownedByCurrentUser(entry.uid)
  )
}

function readLocalStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CacheEntry<T>
    if (Date.now() - parsed.ts >= CACHE_TTL_MS) return null
    if (!ownedByCurrentUser(parsed.uid)) return null
    return parsed.value
  } catch { return null }
}

/**
 * `uid` is the identity the value was fetched *for*, passed explicitly. Reading it here
 * instead would stamp whoever happens to be current when the write lands, which across an
 * identity switch is the wrong user — and this is the copy that survives a reload.
 */
function writeLocalStorage<T>(key: string, value: T, uid: string | null): void {
  try {
    localStorage.setItem(key, JSON.stringify(makeEntryFor(value, uid)))
  } catch { /* quota errors etc. */ }
}

function getAuthenticatedJsonHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getStoredSessionToken()}`,
  }
}

/**
 * Reads a failed response's body without assuming it is JSON. Proxies and caches in front of the
 * API (e.g. Varnish on the client host) answer with empty or plain-text bodies, and calling
 * `response.json()` on those surfaced "Unexpected end of JSON input" to the admin instead of the
 * real failure. [SC-409]
 */
async function readHttpError(response: Response): Promise<{ message: string; code?: string }> {
  const fallback = `HTTP ${response.status}`
  let text = ''
  try {
    text = (await response.text()).trim()
  } catch {
    return { message: fallback }
  }
  if (!text) return { message: fallback }
  try {
    const body = JSON.parse(text)
    const message = typeof body?.error === 'string' && body.error ? body.error : fallback
    return typeof body?.code === 'string' ? { message, code: body.code } : { message }
  } catch {
    // An HTML error page is noise in a toast; short plain text is usually the real reason.
    if (text.startsWith('<')) return { message: fallback }
    return { message: `${fallback}: ${text.slice(0, 200)}` }
  }
}

export class CompanyService {
  /** Clear cached company / admin data (call after login, logout, company change). */
  static clearCache(): void {
    _companyCache = null
    _companyInflight = null
    _adminCache = null
    _adminInflight = null
    try { localStorage.removeItem('wiggin_company_data') } catch { }
    try { localStorage.removeItem('wiggin_is_admin') } catch { }
  }

  /**
   * Drops a cached "not associated with any company" answer, in memory and in localStorage, and
   * leaves a cached company alone. For callers that have just seen the cloud resolve a company
   * for the user (the sign-in, page-load and invitation checks, repairs included): the cached
   * "no company" is then wrong, while a cached company is still right, so the normal path costs
   * no extra read.
   */
  static dropNoCompanyEntry(): void {
    if (_companyCache?.value.noCompany) {
      _companyCache = null
      _companyInflight = null
    }
    try {
      const raw = localStorage.getItem('wiggin_company_data')
      if (raw && (JSON.parse(raw) as CacheEntry<CurrentUserCompanyResult>).value?.noCompany) {
        localStorage.removeItem('wiggin_company_data')
      }
    } catch { /* unreadable entry: nothing to drop */ }
  }

  /**
   * Create a new company (Platform Admin only)
   */
  static async createCompany(companyData: Partial<Company> & { lobbyMapId?: string }): Promise<{ success: boolean; company?: Company; error?: string }> {
    try {
      const result = await Parse.Cloud.run('createCompany', companyData)
      return { success: true, company: result.company }
    } catch (error: any) {
      console.error('Create company error:', error)
      return { success: false, error: error.message || 'Failed to create company' }
    }
  }

  /**
   * Self-serve company creation for users without a company
   * Creates the company and assigns the current user as manager
   */
  static async selfServeCreateCompany(companyData: Partial<Company> & {
    /** SC-422: required; the cloud creates the company's lobby on this map. */
    lobbyMapId?: string
    address_line1?: string
    address_line2?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }): Promise<{ success: boolean; company?: Company; user_role?: string; error?: string }> {
    try {
      const result = await Parse.Cloud.run('selfServeCreateCompany', companyData)
      // The user had no company until now; a cached "no company" answer would hide the new one.
      CompanyService.clearCache()
      return { success: true, company: result.company, user_role: result.user_role }
    } catch (error: any) {
      console.error('Self-serve create company error:', error)
      return { success: false, error: error.message || 'Failed to create company' }
    }
  }

  /**
   * Get company information (platform admin or company member with access)
   */
  static async getCompany(companyId: string): Promise<{ success: boolean; company?: Company; user_role?: string; permissions?: string[]; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getCompany', { company_id: companyId })
      return {
        success: true,
        company: result.company,
        user_role: result.user_role,
        permissions: result.permissions,
      }
    } catch (error: any) {
      console.error('Get company error:', error)
      return { success: false, error: error.message || 'Failed to get company' }
    }
  }

  /**
   * Invite user to company (Company Manager only)
   */
  static async inviteUserToCompany(companyId: string, email: string, role: CompanyUserRole = CompanyUserRole.USER, permissions: string[] = []): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('inviteUserToCompany', {
        company_id: companyId,
        email,
        role,
        permissions
      })
      return { success: true, data: result }
    } catch (error: any) {
      console.error('Invite user to company error:', error)
      return { success: false, error: error.message || 'Failed to invite user' }
    }
  }

  /**
   * Get company users (Company members only).
   * Supports optional server-side pagination. Defaults to pageSize=1000 to return
   * all users in a single call for typical company sizes.
   */
  static async getCompanyUsers(
    companyId: string,
    role?: string,
    isActive: boolean = true,
    page: number = 1,
    pageSize: number = 1000,
  ): Promise<{ success: boolean; users?: CompanyUser[]; total?: number; page?: number; pageSize?: number; hasMore?: boolean; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getCompanyUsers', {
        company_id: companyId,
        role,
        is_active: isActive,
        page,
        pageSize,
      })
      return {
        success: true,
        users: result.users,
        total: result.total,
        page: result.page,
        pageSize: result.pageSize,
        hasMore: result.hasMore,
      }
    } catch (error: any) {
      console.error('Get company users error:', error)
      return { success: false, error: error.message || 'Failed to get company users' }
    }
  }

  /** Page size for multi-page fetches; within cloud `getCompanyUsers` safePageSize cap (50k). */
  private static readonly COMPANY_USERS_PAGE_SIZE = 10_000

  /**
   * Load every page of company users from cloud (in-memory pagination on the server).
   * Use this when the UI must list or count the full roster, not just the first page.
   */
  static async getCompanyUsersAll(
    companyId: string,
    role?: string,
    isActive: boolean = true,
  ): Promise<{ success: boolean; users?: CompanyUser[]; total?: number; error?: string }> {
    const pageSize = CompanyService.COMPANY_USERS_PAGE_SIZE
    const all: CompanyUser[] = []
    let page = 1
    let authoritativeTotal: number | undefined
    const maxPages = 500

    while (page <= maxPages) {
      const r = await CompanyService.getCompanyUsers(companyId, role, isActive, page, pageSize)
      if (!r.success) {
        return { success: false, error: r.error }
      }
      if (authoritativeTotal === undefined && r.total !== undefined) {
        authoritativeTotal = r.total
      }
      const batch = r.users ?? []
      all.push(...batch)

      if (batch.length === 0) {
        break
      }
      if (authoritativeTotal !== undefined && all.length >= authoritativeTotal) {
        break
      }
      if (r.hasMore === false) {
        break
      }
      if (r.hasMore === true) {
        page += 1
        continue
      }
      // Legacy responses may omit hasMore: keep paging when total or a full page implies more rows
      if (authoritativeTotal !== undefined && all.length < authoritativeTotal) {
        page += 1
        continue
      }
      if (batch.length >= pageSize) {
        page += 1
        continue
      }
      break
    }

    const total = authoritativeTotal ?? all.length
    return { success: true, users: all, total }
  }

  /**
   * Get all companies (Platform Admin only)
   */
  static async getAllCompanies(): Promise<{ success: boolean; companies?: Company[]; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getAllCompanies', {})
      return { success: true, companies: result.companies }
    } catch (error: any) {
      console.error('Get all companies error:', error)
      return { success: false, error: error.message || 'Failed to get companies' }
    }
  }

  /**
   * Update company settings (Company Manager or Platform Admin only)
   */
  static async updateCompanySettings(companyId: string, settings: Partial<Company>): Promise<{ success: boolean; company?: Company; error?: string }> {
    try {
      console.log('[CompanyService.updateCompanySettings] companyId:', companyId);
      console.log('[CompanyService.updateCompanySettings] settings:', JSON.stringify(settings, null, 2));

      // parseCloudRun lets a company-closed refusal (SC-409) re-check access. No sign-out on an
      // expired token here, as before.
      const result = await parseCloudRun<any>(
        'updateCompanySettings',
        { company_id: companyId, settings },
        { handleInvalidSession: false }
      )
      CompanyService.clearCache()
      return { success: true, company: result.company }
    } catch (error: any) {
      console.error('Update company settings error:', error)
      return { success: false, error: error.message || 'Failed to update company settings' }
    }
  }

  /**
   * Update company (Platform Admin only)
   */
  static async updateCompany(companyId: string, updates: Partial<Company>): Promise<{ success: boolean; company?: Company; error?: string }> {
    try {
      const result = await Parse.Cloud.run('updateCompany', {
        company_id: companyId,
        updates
      })
      CompanyService.clearCache()
      return { success: true, company: result.company }
    } catch (error: any) {
      console.error('Update company error:', error)
      return { success: false, error: error.message || 'Failed to update company' }
    }
  }

  /**
   * Get company by domain (Public)
   */
  static async getCompanyByDomain(domain: string): Promise<{ success: boolean; company?: Company; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getCompanyByDomain', { domain })
      if (result.success) {
        return { success: true, company: result.company }
      } else {
        return { success: false, error: result.message }
      }
    } catch (error: any) {
      console.error('Get company by domain error:', error)
      return { success: false, error: error.message || 'Failed to get company by domain' }
    }
  }

  /**
   * Update company user role and permissions (Company Manager or Platform Admin only)
   */
  static async updateCompanyUser(companyId: string, userId: string, updates: {
    /** A legacy role string or a custom role's key. */
    role?: CompanyUserRole | string;
    permissions?: string[];
    department?: string;
    title?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('updateCompanyUser', {
        company_id: companyId,
        user_id: userId,
        ...updates
      })
      return { success: true, data: result }
    } catch (error: any) {
      console.error('Update company user error:', error)
      return { success: false, error: error.message || 'Failed to update company user' }
    }
  }

  /**
   * Remove user from company (Company Manager or Platform Admin only)
   */
  static async removeCompanyUser(companyId: string, userId: string): Promise<{
    success: boolean;
    data?: any;
    records_removed?: number;
    pending_records_removed?: number;
    error?: string;
    message?: string;
  }> {
    try {
      const result = await Parse.Cloud.run('removeCompanyUser', {
        company_id: companyId,
        user_id: userId
      })
      return {
        success: true,
        data: result,
        records_removed: result.records_removed,
        pending_records_removed: result.pending_records_removed,
        message: result.message
      }
    } catch (error: any) {
      console.error('Remove company user error:', error)
      return { success: false, error: error.message || 'Failed to remove company user' }
    }
  }

  /**
   * Get current user's company.
   * Results are cached in-memory (5 min TTL) and localStorage so
   * multiple components mounting at the same time share a single call.
   */
  static async getCurrentUserCompany(forceRefresh = false): Promise<CurrentUserCompanyResult> {
    // 1. In-memory cache
    if (!forceRefresh && isFresh(_companyCache)) return _companyCache.value

    // 2. Deduplicate concurrent callers
    if (!forceRefresh && _companyInflight) return _companyInflight

    // 3. localStorage fallback (survives page reload for the TTL)
    if (!forceRefresh) {
      const stored = readLocalStorage<CurrentUserCompanyResult>('wiggin_company_data')
      if (stored) {
        _companyCache = makeEntry(stored)
        return stored
      }
    }

    // 4. Network fetch (single in-flight promise)
    _companyInflight = (async () => {
      // Captured before the await, not after it — see makeEntryFor.
      const requesterUid = currentUserId()
      try {
        if (!Parse.User.current()?.getSessionToken()) {
          return { success: false as const, error: 'Not authenticated', lookupFailed: true }
        }
        const result = await parseCloudRun<{
          success: boolean
          company?: Company
          user_role?: string
          role_name?: string | null
          effective_role_key?: string | null
          role_is_default?: boolean
          permissions?: string[]
          message?: string
          no_company?: boolean
        }>('getCurrentUserCompany', {})
        const value: CurrentUserCompanyResult = result.success
          ? {
              success: true as const,
              company: result.company,
              user_role: result.user_role,
              ...(result.role_name ? { role_name: result.role_name } : {}),
              ...(result.effective_role_key ? { effective_role_key: result.effective_role_key } : {}),
              ...(result.role_is_default === true ? { role_is_default: true } : {}),
              permissions: result.permissions,
            }
          : {
              success: false as const,
              error: result.message,
              ...(result.no_company === true ? { noCompany: true as const } : {}),
            }
        // Identity switched under us (invitation redemption calls Parse.User.become while an
        // admin is signed in): hand the result to the awaiting caller, but do not cache it —
        // the localStorage copy would otherwise outlive the reload and serve one user's
        // company to another.
        if (!stillSameRequester(requesterUid)) {
          console.warn('[CompanyService] Discarding company result fetched for a previous user')
          return value
        }
        // A refusal or error is never cached: the next read must ask again. "Not associated with
        // any company" is not one: it is a normal answer that only changes when this browser
        // assigns a company (every such path calls clearCache()), so repeat readers share it.
        if (value.success || value.noCompany) {
          _companyCache = makeEntryFor(value, requesterUid)
          writeLocalStorage('wiggin_company_data', value, requesterUid)
        }
        return value
      } catch (error: any) {
        console.error('Get current user company error:', error)
        return {
          success: false as const,
          error: error.message || 'Failed to get current user company',
          lookupFailed: true,
        }
      } finally {
        _companyInflight = null
      }
    })()

    return _companyInflight
  }

  /**
   * Check if user has company permission
   */
  static async hasCompanyPermission(companyId: string, permission: string): Promise<boolean> {
    try {
      const currentUser = Parse.User.current()
      if (!currentUser) return false

      const profileQuery = new Parse.Query('Profile')
      profileQuery.equalTo('userId', currentUser.id)
      const profile = await profileQuery.first()

      // Platform admins have all permissions
      if (profile && profile.get('role') === 'admin') {
        return true
      }

      // Check company-specific permissions
      const companyUserQuery = new Parse.Query('CompanyUser')
      companyUserQuery.equalTo('company_id', companyId)
      companyUserQuery.equalTo('user_id', currentUser.id)
      companyUserQuery.equalTo('is_active', true)
      const companyUser = await companyUserQuery.first()

      if (!companyUser) return false

      const userRole = companyUser.get('role')
      const userPermissions = companyUser.get('permissions') || []

      // Company managers have all permissions
      if (userRole === 'manager') {
        return true
      }

      // Check specific permission
      return userPermissions.includes(permission)
    } catch (error) {
      console.error('Check company permission error:', error)
      return false
    }
  }

  /**
   * Sync unassigned users to company based on domain matching
   */
  static async syncUnassignedUsers(companyId: string): Promise<{
    success: boolean;
    syncedCount?: number;
    syncedUsers?: Array<{ email: string; userId: string; resynced?: boolean }>;
    skippedUsers?: Array<{ email: string; reason: string }>;
    error?: string;
    message?: string;
  }> {
    try {
      const result = await Parse.Cloud.run('syncUnassignedUsers', {
        company_id: companyId
      })
      // The signed-in user can be one of the users just assigned (a platform admin with no
      // company); a cached "no company" answer would hide it.
      if (result.syncedCount > 0) CompanyService.clearCache()
      return {
        success: true,
        syncedCount: result.syncedCount,
        syncedUsers: result.syncedUsers || [],
        skippedUsers: result.skippedUsers || [],
        message: result.message
      }
    } catch (error: any) {
      console.error('Sync unassigned users error:', error)
      return {
        success: false,
        error: error.message || 'Failed to sync unassigned users'
      }
    }
  }

  /**
   * Check if current user is a platform admin.
   * Cached in-memory (5 min TTL) — admin status virtually never changes mid-session.
   */
  static async isPlatformAdmin(): Promise<boolean> {
    if (isFresh(_adminCache)) return _adminCache.value
    if (_adminInflight) return _adminInflight

    _adminInflight = (async () => {
      // Captured before the await, not after it — see makeEntryFor. Caching an admin's
      // `true` under the invitee's uid is the same leak with worse consequences.
      const requesterUid = currentUserId()
      try {
        const currentUser = Parse.User.current()
        if (!currentUser) return false

        const profileQuery = new Parse.Query('Profile')
        profileQuery.equalTo('userId', currentUser.id)
        const profile = await profileQuery.first()

        const isAdmin = !!(profile && profile.get('role') === 'admin')
        if (!stillSameRequester(requesterUid)) {
          console.warn('[CompanyService] Discarding admin result fetched for a previous user')
          return isAdmin
        }
        _adminCache = makeEntryFor(isAdmin, requesterUid)
        return isAdmin
      } catch (error) {
        console.error('Check platform admin error:', error)
        return false
      } finally {
        _adminInflight = null
      }
    })()

    return _adminInflight
  }

  /**
   * Check if current user is a company manager
   */
  static async isCompanyManager(companyId: string): Promise<boolean> {
    try {
      const currentUser = Parse.User.current()
      if (!currentUser) return false

      const companyUserQuery = new Parse.Query('CompanyUser')
      companyUserQuery.equalTo('company_id', companyId)
      companyUserQuery.equalTo('user_id', currentUser.id)
      companyUserQuery.equalTo('role', 'manager')
      const companyUser = await companyUserQuery.first()

      return !!companyUser
    } catch (error) {
      console.error('Check company manager error:', error)
      return false
    }
  }

  /**
   * Suspend or reactivate a company (Platform Admin only).
   *
   * Goes through the server rather than the cloud function so that suspension is enforced where
   * it matters (room joins, lobby, chat token) and live sessions are disconnected in the same
   * request. `status` is accepted for caller compatibility but not sent: billing owns
   * `Company.status`, and the server route only takes `is_active` and `reason`. [SC-409]
   *
   * `code` is passed through so the caller can tell `COMPANY_DELETION_IN_PROGRESS` (the admin's
   * list is stale: the company must be finished, not reactivated) from a plain failure.
   */
  static async updateCompanyStatus(companyId: string, updates: {
    is_active?: boolean;
    status?: string;
    reason?: string;
  }): Promise<{ success: boolean; company?: Company; error?: string; code?: string }> {
    try {
      const response = await fetch(
        `${getServerBaseUrl()}/api/company/${encodeURIComponent(companyId)}/status`,
        {
          method: 'POST',
          headers: getAuthenticatedJsonHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            is_active: updates.is_active,
            ...(updates.reason ? { reason: updates.reason } : {}),
          }),
        }
      )

      if (!response.ok) {
        const { message, code } = await readHttpError(response)
        return { success: false, error: message, ...(code ? { code } : {}) }
      }

      const result = await response.json()
      return { success: true, company: result.company }
    } catch (error: any) {
      console.error('Update company status error:', error)
      return { success: false, error: error.message || 'Failed to update company status' }
    }
  }

  /**
   * Check if current user has access to a company
   */
  static async hasCompanyAccess(companyId: string): Promise<boolean> {
    try {
      const currentUser = Parse.User.current()
      if (!currentUser) return false

      const companyUserQuery = new Parse.Query('CompanyUser')
      companyUserQuery.equalTo('company_id', companyId)
      companyUserQuery.equalTo('user_id', currentUser.id)
      const companyUser = await companyUserQuery.first()

      return !!companyUser
    } catch (error) {
      console.error('Check company access error:', error)
      return false
    }
  }

  /**
   * Validate email for invitation and check if user exists or is part of another company
   */
  static async validateEmailForInvitation(
    email: string,
    companyId: string
  ): Promise<{
    success: boolean;
    userExists: boolean;
    isPartOfAnotherCompany: boolean;
    isPartOfThisCompany: boolean;
    canInvite: boolean;
    error?: string;
    message?: string;
  }> {
    try {
      const result = await Parse.Cloud.run('validateEmailForInvitation', {
        email: email.toLowerCase(),
        company_id: companyId
      })
      return result
    } catch (error: any) {
      console.error('Email validation error:', error)
      return {
        success: false,
        userExists: false,
        isPartOfAnotherCompany: false,
        isPartOfThisCompany: false,
        canInvite: false,
        error: error.message || 'Failed to validate email'
      }
    }
  }

  /**
   * Send email via server endpoint (EXACTLY matches EndpointTestingPanel approach)
   */
  private static async sendEmailViaServer(
    endpoint: string,
    data: any
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      console.log(`[CompanyService] Preparing to send email via server endpoint: ${endpoint}`, data)

      // Get current user session token for authentication (EXACTLY like EndpointTestingPanel)
      const { authService } = await import('./back4app')
      const currentUser = authService.getCurrentUser?.()
      const sessionToken = currentUser?.getSessionToken?.()

      if (!sessionToken) {
        console.error('[CompanyService] No session token available for email sending')
        return {
          success: false,
          error: 'You must be logged in to send emails'
        }
      }

      // Determine server base URL (EXACTLY like EndpointTestingPanel)
      const baseUrl = getServerBaseUrl()
      // Use EXACT same isDev check as EndpointTestingPanel
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev ? `/api/email/${endpoint}` : `${baseUrl}/api/email/${endpoint}`

      console.log(`[CompanyService] Calling email endpoint: ${fullUrl}`, {
        isDev,
        baseUrl,
        endpoint,
        hasSessionToken: !!sessionToken
      })

      // Call the server endpoint (EXACTLY like EndpointTestingPanel)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify(data)
      })

      console.log(`[CompanyService] Email endpoint response status: ${response.status}`)

      const result = await response.json()
      console.log(`[CompanyService] Email endpoint response:`, result)

      if (!response.ok) {
        console.error(`[CompanyService] Email endpoint returned error:`, {
          status: response.status,
          error: result.error,
          message: result.message
        })
        return {
          success: false,
          error: result.error || result.message || `Server returned ${response.status}`
        }
      }

      const success = result.success || false
      const messageId = result.result?.messageId || result.messageId

      console.log(`[CompanyService] Email sent successfully:`, {
        success,
        messageId,
        endpoint
      })

      return {
        success,
        messageId
      }
    } catch (error: any) {
      console.error('[CompanyService] Error sending email via server:', error)
      console.error('[CompanyService] Error details:', {
        message: error.message,
        stack: error.stack,
        endpoint,
        data
      })
      return {
        success: false,
        error: error.message || 'Failed to send email'
      }
    }
  }

  /**
   * Invite user to company with automatic user creation if needed
   * Now sends emails from client via server endpoints (like EndpointTestingPanel)
   */
  static async inviteUserToCompanyAdvanced(
    companyId: string,
    userData: {
      email: string
      /** Omitted when the inviter chose none for a member who is already in: cloud keeps theirs. */
      role?: string
      department?: string
      title?: string
      /** Fill a brand-new invitee's profile (SC-438); an existing user's names are kept. */
      firstName?: string
      lastName?: string
      permissions?: string[]
      invitationType: 'email' | 'existing_user'
      selectedUser?: any
    }
  ): Promise<{
    success: boolean;
    user_id?: string;
    is_new_user?: boolean;
    email_sent?: boolean;
    error?: string;
    message?: string;
  }> {
    try {
      // Call cloud function to create user/invitation (but don't send emails)
      const result = await Parse.Cloud.run('inviteUserToCompany', {
        company_id: companyId,
        email: userData.email,
        role: userData.role,
        department: userData.department,
        title: userData.title,
        firstName: userData.firstName,
        lastName: userData.lastName,
        permissions: userData.permissions || [],
        invitationType: userData.invitationType,
        userId: userData.selectedUser?.id,
        sendWelcomeEmail: false // Don't send emails from cloud function
      })

      if (!result.success) {
        return result
      }

      // If invitation was successful, send emails from client via server
      console.log('[CompanyService] Invitation result:', {
        success: result.success,
        hasInvitationData: !!result.invitation_data,
        isNewUser: result.is_new_user,
        invitationData: result.invitation_data
      })

      if (result.invitation_data) {
        const { invitation_data } = result

        // Get current user (inviter) ID
        const { authService } = await import('./back4app')
        const currentUser = authService.getCurrentUser?.()
        const inviterUserId = currentUser?.id

        console.log('[CompanyService] Sending company invitation email with data:', {
          inviteeEmail: invitation_data.inviteeEmail,
          inviteeUserId: result.user_id,
          companyId: companyId,
          inviterUserId: inviterUserId,
          role: invitation_data.role,
          hasInvitationLink: !!invitation_data.invitationLink
        })

        // This is the only email an invitee gets (titled "Welcome to …!"). A separate account-created
        // email used to follow for new users; it was redundant, since password setup happens on accept.
        // Send company invitation email using new centralized pattern (userId/companyId)
        // Falls back to old pattern if userIds not available
        const invitationEmailResult = await this.sendEmailViaServer('company-invitation',
          result.user_id && inviterUserId && companyId
            ? {
              // New centralized pattern
              inviteeUserId: result.user_id,
              companyId: companyId,
              inviterUserId: inviterUserId,
              invitationLink: invitation_data.invitationLink,
              role: invitation_data.role
            }
            : {
              // Old pattern (backward compatibility)
              inviteeEmail: invitation_data.inviteeEmail,
              inviteeName: invitation_data.inviteeName,
              companyName: invitation_data.companyName,
              inviterName: invitation_data.inviterName,
              invitationLink: invitation_data.invitationLink,
              role: invitation_data.role
            }
        )

        console.log('[CompanyService] Company invitation email result:', invitationEmailResult)

        if (invitationEmailResult.success) {
          result.email_sent = true
          result.email_messageId = invitationEmailResult.messageId
          console.log('[CompanyService] ✓ Company invitation email sent successfully')
        } else {
          console.error('[CompanyService] ✗ Failed to send company invitation email:', invitationEmailResult.error)
          result.email_error = invitationEmailResult.error
          // Don't fail the whole operation if email fails
        }
      } else {
        console.warn('[CompanyService] No invitation_data in result, cannot send emails:', result)
      }

      return result
    } catch (error: any) {
      console.error('Invite user to company error:', error)
      return {
        success: false,
        error: error.message || 'Failed to invite user to company'
      }
    }
  }

  /**
   * Resend a company invitation to a user who has not finished onboarding.
   *
   * Replaces the remove-and-re-invite workaround: the cloud function reuses the
   * invitee's existing token when it is still valid, so the link in the email they
   * already have keeps working. Pass `invalidatePrevious` only when the old link
   * should be killed (e.g. the email is believed compromised).
   */
  static async resendInvitation(
    userId: string,
    companyId: string,
    options: { invalidatePrevious?: boolean } = {},
  ): Promise<{
    success: boolean
    tokenReused?: boolean
    emailSent?: boolean
    error?: string
  }> {
    try {
      const result = await Parse.Cloud.run('resendWelcomeEmail', {
        user_id: userId,
        company_id: companyId,
        invalidate_previous: options.invalidatePrevious === true,
      })

      if (!result?.invitation_data) {
        return {
          success: false,
          error: result?.error || 'The server did not return invitation details.',
        }
      }

      const { invitation_data } = result
      const { authService } = await import('./back4app')
      const inviterUserId = authService.getCurrentUser?.()?.id

      const emailResult = await this.sendEmailViaServer(
        'company-invitation',
        inviterUserId && companyId
          ? {
              inviteeUserId: userId,
              companyId,
              inviterUserId,
              invitationLink: invitation_data.invitationLink,
              role: invitation_data.role,
            }
          : {
              inviteeEmail: invitation_data.inviteeEmail,
              inviteeName: invitation_data.inviteeName,
              companyName: invitation_data.companyName,
              inviterName: invitation_data.inviterName,
              invitationLink: invitation_data.invitationLink,
              role: invitation_data.role,
            },
      )

      if (!emailResult.success) {
        return { success: false, tokenReused: result.token_reused, error: emailResult.error }
      }

      return { success: true, tokenReused: result.token_reused, emailSent: true }
    } catch (error: any) {
      console.error('[CompanyService] Resend invitation error:', error)
      return { success: false, error: error?.message || 'Failed to resend the invitation' }
    }
  }

  /**
   * Add user to company with dynamic billing
   */
  static async addUserWithBilling(
    companyId: string,
    userData: {
      email: string
      name: string
      role: string
      department?: string
      title?: string
      invitationType?: string
      selectedUser?: any
    },
    addedBy?: string
  ): Promise<{ success: boolean; billing_impact?: any; pending_user_id?: string; requires_payment?: boolean; error?: string }> {
    try {
      const result = await Parse.Cloud.run('addUserWithBilling', {
        company_id: companyId,
        user_email: userData.email,
        user_name: userData.name,
        role: userData.role,
        department: userData.department,
        title: userData.title,
        added_by: addedBy,
        billing_preview: false
      })
      return {
        success: true,
        billing_impact: result.billing_impact,
        pending_user_id: result.pending_user_id,
        requires_payment: result.requires_payment
      }
    } catch (error: any) {
      console.error('Add user with billing error:', error)
      return { success: false, error: error.message || 'Failed to add user with billing' }
    }
  }

  /**
   * Remove user from company with dynamic billing
   */
  static async removeUserWithBilling(
    companyId: string,
    userId: string,
    removedBy?: string,
    gracePeriodHours: number = 24
  ): Promise<{ success: boolean; billing_impact?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('removeUserWithBilling', {
        company_id: companyId,
        user_id: userId,
        removed_by: removedBy,
        grace_period_hours: gracePeriodHours,
        billing_preview: false
      })
      return { success: true, billing_impact: result.billing_impact }
    } catch (error: any) {
      console.error('Remove user with billing error:', error)
      return { success: false, error: error.message || 'Failed to remove user with billing' }
    }
  }

  /**
   * Remove multiple users from company with dynamic billing
   */
  static async removeUsersWithBilling(
    companyId: string,
    userIds: string[],
    removedBy?: string,
    gracePeriodHours: number = 0
  ): Promise<{ success: boolean; billing_impact?: any; removed_count?: number; failed_removals?: any[]; error?: string }> {
    try {
      const result = await Parse.Cloud.run('removeUsersWithBilling', {
        company_id: companyId,
        user_ids: userIds,
        removed_by: removedBy,
        grace_period_hours: gracePeriodHours,
        billing_preview: false
      })
      return {
        success: true,
        billing_impact: result.billing_impact,
        removed_count: result.removed_count,
        failed_removals: result.failed_removals || []
      }
    } catch (error: any) {
      console.error('Remove users with billing error:', error)
      return { success: false, error: error.message || 'Failed to remove users with billing' }
    }
  }

  /**
   * Get billing impact preview for user changes
   */
  static async getBillingImpactPreview(
    companyId: string,
    action: 'addition' | 'removal',
    userCountChange: number = 1
  ): Promise<{ success: boolean; billing_impact?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getBillingImpactPreview', {
        company_id: companyId,
        action: action,
        user_count_change: userCountChange
      })
      // The cloud function reports a missing billing account as `{ success: false, error }`
      // with an HTTP 200, so hardcoding `success: true` here turned that into
      // `{ success: true, billing_impact: undefined }` — and BillingImpactPreview rendered
      // "No billing impact data available." instead of the actual reason. Only an explicit
      // `false` is treated as failure: several cloud functions omit `success` entirely.
      if (result?.success === false) {
        return { success: false, error: result.error || 'Failed to get billing impact preview' }
      }
      return { success: true, billing_impact: result.billing_impact }
    } catch (error: any) {
      console.error('Get billing impact preview error:', error)
      return { success: false, error: error.message || 'Failed to get billing impact preview' }
    }
  }

  /**
   * Get billing impact preview for bulk user removal
   */
  static async getBulkRemovalBillingImpactPreview(
    companyId: string,
    userIds: string[]
  ): Promise<{ success: boolean; billing_impact?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getBulkRemovalBillingImpactPreview', {
        company_id: companyId,
        user_ids: userIds
      })
      // Same trap as getBillingImpactPreview above, feeding the same dialog.
      if (result?.success === false) {
        return {
          success: false,
          error: result.error || 'Failed to get bulk removal billing impact preview',
        }
      }
      return { success: true, billing_impact: result.billing_impact }
    } catch (error: any) {
      console.error('Get bulk removal billing impact preview error:', error)
      return { success: false, error: error.message || 'Failed to get bulk removal billing impact preview' }
    }
  }

  /**
   * Get company billing status
   */
  static async getBillingStatus(companyId: string): Promise<{ success: boolean; billing_status?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getCompanyBillingStatus', {
        company_id: companyId
      })
      return { success: true, billing_status: result.billing_status }
    } catch (error: any) {
      console.error('Get billing status error:', error)
      return { success: false, error: error.message || 'Failed to get billing status' }
    }
  }

  /**
   * Get company credit information
   */
  static async getCompanyCreditInfo(companyId: string): Promise<{ success: boolean; credit_info?: any; error?: string }> {
    try {
      // Source credit info from Stripe via billing status
      const result = await Parse.Cloud.run('getCompanyBillingStatus', {
        company_id: companyId
      })

      const billing = result?.billing_status || {}
      const creditInfo = {
        current_balance: billing.current_balance ?? 0,
        stripe_balance_available: billing.stripe_balance_available ?? false,
        total_earned: billing.total_earned ?? 0,
        total_used: billing.total_used ?? 0,
      }

      return { success: true, credit_info: creditInfo }
    } catch (error: any) {
      console.error('Get company credit info error:', error)
      return { success: false, error: error.message || 'Failed to get company credit info' }
    }
  }

  /**
   * Cancel grace period for user reactivation
   */
  static async cancelGracePeriod(companyId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await Parse.Cloud.run('cancelGracePeriod', {
        company_id: companyId,
        user_id: userId
      })
      return { success: true }
    } catch (error: any) {
      console.error('Cancel grace period error:', error)
      return { success: false, error: error.message || 'Failed to cancel grace period' }
    }
  }

  /**
   * Get grace period status for a user
   */
  static async getGracePeriodStatus(companyId: string, userId: string): Promise<{ success: boolean; grace_period_status?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getGracePeriodStatus', {
        company_id: companyId,
        user_id: userId
      })
      return { success: true, grace_period_status: result }
    } catch (error: any) {
      console.error('Get grace period status error:', error)
      return { success: false, error: error.message || 'Failed to get grace period status' }
    }
  }

  // Pending User Management
  static async getPendingUsers(companyId: string): Promise<{ success: boolean; pending_users?: any[]; error?: string }> {
    try {
      const result = await Parse.Cloud.run('getPendingUsers', {
        company_id: companyId
      })
      return { success: true, pending_users: result.pending_users }
    } catch (error: any) {
      console.error('Get pending users error:', error)
      return { success: false, error: error.message }
    }
  }

  static async processPendingUserPayment(companyId: string, paymentData: {
    pending_user_ids: string[]
    payment_method_id?: string
    use_credits?: boolean
    created_by?: string
    success_url?: string
    cancel_url?: string
  }): Promise<{ success: boolean; checkout_url?: string; session_id?: string; error?: string }> {
    try {
      const result = await Parse.Cloud.run('processPendingUserPayment', {
        company_id: companyId,
        ...paymentData
      })
      return { success: true, ...result }
    } catch (error: any) {
      console.error('Process pending user payment error:', error)
      return { success: false, error: error.message }
    }
  }

  static async cancelPendingUser(pendingUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await Parse.Cloud.run('cancelPendingUser', {
        pending_user_id: pendingUserId
      })
      return { success: true }
    } catch (error: any) {
      console.error('Cancel pending user error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Initialize billing period dates for a company
   */
  static async initializeBillingPeriodDates(companyId: string): Promise<{ success: boolean; period_dates?: any; error?: string }> {
    try {
      const result = await Parse.Cloud.run('initializeBillingPeriodDates', {
        company_id: companyId
      })
      return { success: true, period_dates: result.period_dates }
    } catch (error: any) {
      console.error('Initialize billing period dates error:', error)
      return { success: false, error: error.message || 'Failed to initialize billing period dates' }
    }
  }

  /**
   * Get company removal preview (Platform Admin only)
   */
  static async getCompanyRemovalPreview(companyId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(
        `${getServerBaseUrl()}/api/company/${encodeURIComponent(companyId)}/removal-preview`,
        {
          method: 'GET',
          headers: getAuthenticatedJsonHeaders(),
          credentials: 'include',
        }
      )

      if (!response.ok) {
        const { message } = await readHttpError(response)
        throw new Error(message)
      }

      const result = await response.json()
      return { success: true, data: result }
    } catch (error: any) {
      console.error('Get company removal preview error:', error)
      return { success: false, error: error.message || 'Failed to get company removal preview' }
    }
  }

  /**
   * Remove company completely (Platform Admin only).
   *
   * `code` is passed through so the caller can tell a retryable partial deletion
   * (`STRIPE_CANCEL_FAILED`: the company is left `deleting`) from a hard failure.
   */
  static async removeCompany(companyId: string, reason?: string): Promise<{ success: boolean; data?: any; error?: string; code?: string }> {
    try {
      const response = await fetch(`${getServerBaseUrl()}/api/company/${encodeURIComponent(companyId)}`, {
        method: 'DELETE',
        headers: getAuthenticatedJsonHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          reason: reason || 'Company removal requested by admin'
        })
      })

      if (!response.ok) {
        const { message, code } = await readHttpError(response)
        return { success: false, error: message, ...(code ? { code } : {}) }
      }

      const result = await response.json()
      return { success: true, data: result }
    } catch (error: any) {
      console.error('Remove company error:', error)
      return { success: false, error: error.message || 'Failed to remove company' }
    }
  }
} 
